import { useState } from 'react';
import { Link } from 'react-router-dom';
import ImageCarousel from '../../../components/feature/ImageCarousel';

interface ProductModel {
  id: string;
  name: string;
  size: string;
  capacity: string;
  features: string[];
  price: string;
  image: string;
  highlight: string;
  featured?: boolean;
  carouselImages: {
    interior: string;
    floorPlan: string;
    exterior: string;
  };
  technicalSpecs: {
    weight: string;
    windResistance: string;
    snowLoad: string;
    seismicRating: string;
    insulationRating: string;
    soundproofing: string;
  };
  sellingPoints: string;
}

export default function ModelVariants() {
  const [selectedProduct, setSelectedProduct] = useState<ProductModel | null>(null);

  const models: ProductModel[] = [
    {
      id: 'z15',
      name: 'Z-15 Compact',
      size: '15㎡',
      capacity: '2 persons',
      features: ['Queen bed', 'Full bathroom', 'Kitchenette', 'Smart controls'],
      price: 'Coming Soon',
      image: '/images/ai-compact-space-capsule-pod-exterior-view--b95b80.webp',
      highlight: 'Best for couples',
      carouselImages: {
        interior: '/images/ai-futuristic-compact-capsule-hotel-interio-647822.webp',
        floorPlan: '/images/ai-professional-architectural-floor-plan-of-69e95e.webp',
        exterior: '/images/ai-compact-futuristic-space-capsule-pod-in--a45e7b.webp'
      },
      technicalSpecs: {
        weight: '3,200 kg',
        windResistance: 'Grade 10 (28.5-32.6 m/s)',
        snowLoad: '0.6 kN/㎡',
        seismicRating: 'Grade 8',
        insulationRating: 'R-4.8',
        soundproofing: '38 dB reduction'
      },
      sellingPoints: 'Revolutionary plug-and-play hospitality solution requiring zero foundation. Arrives fully furnished and pre-wired. Simply position on level ground and connect utilities. Perfect for glamping sites, resorts, or urban hospitality projects. Deploy your guest accommodation in hours, not months.'
    },
    {
      id: 'z20',
      name: 'Z-20 Standard',
      size: '20㎡',
      capacity: '2-3 persons',
      features: ['Queen bed + sofa bed', 'Full bathroom', 'Living area', 'Entertainment system'],
      price: 'Coming Soon',
      image: '/images/ai-standard-space-capsule-pod-exterior-20-s-fa75a1.webp',
      highlight: 'Most popular',
      featured: true,
      carouselImages: {
        interior: '/images/ai-spacious-futuristic-capsule-hotel-interi-32134d.webp',
        floorPlan: '/images/ai-detailed-architectural-floor-plan-of-20--3c9639.webp',
        exterior: '/images/ai-standard-futuristic-space-capsule-pod-wi-bee8ad.webp'
      },
      technicalSpecs: {
        weight: '4,500 kg',
        windResistance: 'Grade 11 (32.7-36.9 m/s)',
        snowLoad: '0.8 kN/㎡',
        seismicRating: 'Grade 8',
        insulationRating: 'R-5.2',
        soundproofing: '42 dB reduction'
      },
      sellingPoints: 'Most popular hospitality capsule with no foundation required. Advanced modular design allows rapid deployment. All amenities pre-installed including entertainment systems and smart controls. Relocatable and expandable. Ideal for boutique hotels, glamping resorts, or temporary accommodations. Set up complete guest suites in a single day.'
    },
    {
      id: 'z25',
      name: 'Z-25 Deluxe',
      size: '25㎡',
      capacity: '4 persons',
      features: ['Separate bedroom', 'Luxury bathroom', 'Full kitchen', 'Premium finishes'],
      price: 'Coming Soon',
      image: '/images/ai-deluxe-space-capsule-pod-exterior-view-2-69152c.webp',
      highlight: 'Maximum comfort',
      carouselImages: {
        interior: '/images/ai-luxury-futuristic-capsule-suite-interior-9376c2.webp',
        floorPlan: '/images/ai-comprehensive-architectural-floor-plan-o-39f0b2.webp',
        exterior: '/images/ai-deluxe-futuristic-space-capsule-with-ext-b00d4a.webp'
      },
      technicalSpecs: {
        weight: '6,800 kg',
        windResistance: 'Grade 12 (37.0-41.4 m/s)',
        snowLoad: '1.0 kN/㎡',
        seismicRating: 'Grade 9',
        insulationRating: 'R-5.8',
        soundproofing: '48 dB reduction'
      },
      sellingPoints: 'Premium luxury capsule with zero foundation requirements. Fully equipped with high-end appliances and smart home technology. Advanced leveling system adapts to any terrain. Perfect for luxury resorts, eco-lodges, or exclusive glamping destinations. Deliver five-star accommodations without traditional construction timelines or site disruption.'
    }
  ];

  const openModal = (product: ProductModel) => {
    setSelectedProduct(product);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedProduct(null);
    document.body.style.overflow = 'unset';
  };

  const getCarouselImages = (product: ProductModel) => {
    return [
      { url: product.carouselImages.interior, label: 'Interior View' },
      { url: product.carouselImages.exterior, label: 'Exterior View' }
    ];
  };

  return (
    <section id="models" className="relative py-24 lg:py-32 w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-20">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-thin mb-6">
            Model <span className="text-cyan-400">Variants</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
            Choose the perfect size and configuration for your hospitality needs
          </p>
        </div>

        {/* Models Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {models.map((model, index) => (
            <div 
              key={index}
              onClick={() => openModal(model)}
              className={`relative bg-gradient-to-br from-gray-900/80 to-gray-800/50 border rounded-3xl overflow-hidden group hover:shadow-[0_0_40px_rgba(0,217,255,0.3)] transition-all duration-300 cursor-pointer ${
                model.featured ? 'border-cyan-500 lg:scale-105' : 'border-cyan-500/30'
              }`}
            >
              {/* Featured Badge */}
              {model.featured && (
                <div className="absolute top-4 right-4 z-10 px-4 py-2 bg-cyan-500 text-black text-sm font-medium rounded-full">
                  {model.highlight}
                </div>
              )}

              {/* Image */}
              <div className="relative w-full h-64 overflow-hidden">
                <img 
                  src={model.image}
                  alt={model.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent"></div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="text-2xl font-medium mb-2 text-white">{model.name}</h3>
                {!model.featured && (
                  <p className="text-sm text-cyan-400 mb-4">{model.highlight}</p>
                )}
                
                {/* Specs */}
                <div className="flex items-center gap-6 mb-6 text-gray-400">
                  <div className="flex items-center gap-2">
                    <i className="ri-ruler-line text-cyan-400"></i>
                    <span className="text-sm">{model.size}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-user-line text-cyan-400"></i>
                    <span className="text-sm">{model.capacity}</span>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {model.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs text-cyan-400"></i>
                      </div>
                      <span className="text-sm text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Price */}
                <div className="pt-6 border-t border-gray-700/50">
                  <div className="text-2xl font-medium text-cyan-400 mb-4">{model.price}</div>
                  <div className="text-center py-3 bg-cyan-500/10 border border-cyan-500/30 rounded-full group-hover:bg-cyan-500/20 transition-all">
                    <span className="text-cyan-400 text-sm font-medium">Click to View Details</span>
                    <i className="ri-arrow-right-line text-cyan-400 ml-2"></i>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Customisation Note */}
        <div className="mt-16 bg-gradient-to-br from-cyan-500/10 to-transparent border border-cyan-500/30 rounded-3xl p-8 lg:p-12 text-center">
          <div className="w-20 h-20 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <i className="ri-settings-3-line text-5xl text-cyan-400"></i>
          </div>
          <h3 className="text-2xl font-medium mb-4">Custom Configurations Available</h3>
          <p className="text-gray-400 max-w-2xl mx-auto mb-6">
            All models can be customised with different finishes, layouts, and additional features to match your brand and requirements.
          </p>
          <Link 
            to="/contact"
            className="inline-block px-8 py-3 bg-transparent border-2 border-cyan-500 text-cyan-400 text-base font-medium rounded-full hover:bg-cyan-500/10 transition-all whitespace-nowrap cursor-pointer"
          >
            Discuss Customisation
          </Link>
        </div>
      </div>

      {/* Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={closeModal}
        >
          <div
            className="bg-gray-900/95 border border-cyan-500/30 rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-900/95 backdrop-blur-xl border-b border-cyan-500/20 p-6 flex items-center justify-between z-10">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white">{selectedProduct.name}</h3>
                <p className="text-cyan-400 text-sm mt-1">{selectedProduct.highlight}</p>
              </div>
              <button
                onClick={closeModal}
                className="w-10 h-10 flex items-center justify-center bg-gray-800/60 hover:bg-gray-700/60 rounded-full transition-all cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-white"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 sm:p-8 space-y-8">
              {/* Image Carousel - New Component */}
              <ImageCarousel 
                images={getCarouselImages(selectedProduct)} 
                className="h-96"
              />

              {/* Technical Specifications Table */}
              <div className="bg-gray-800/40 border border-cyan-500/20 rounded-2xl p-6">
                <h4 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-settings-3-line text-cyan-400"></i>
                  Technical Specifications
                </h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  {Object.entries(selectedProduct.technicalSpecs).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center p-4 bg-gray-900/40 rounded-xl">
                      <span className="text-gray-400 text-sm capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span className="text-white font-semibold text-sm">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Selling Points */}
              <div className="bg-gradient-to-r from-cyan-500/20 to-cyan-600/20 border border-cyan-400/30 rounded-2xl p-6">
                <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <i className="ri-lightbulb-flash-line text-cyan-400"></i>
                  Plug & Play Installation
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {selectedProduct.sellingPoints}
                </p>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-800/40 border border-cyan-500/20 rounded-xl p-4 text-center">
                  <i className="ri-layout-grid-line text-2xl text-cyan-400 mb-2"></i>
                  <div className="text-xs text-gray-400 mb-1">Floor Area</div>
                  <div className="text-sm font-semibold text-white">{selectedProduct.size}</div>
                </div>
                <div className="bg-gray-800/40 border border-cyan-500/20 rounded-xl p-4 text-center">
                  <i className="ri-group-line text-2xl text-cyan-400 mb-2"></i>
                  <div className="text-xs text-gray-400 mb-1">Capacity</div>
                  <div className="text-sm font-semibold text-white break-words">{selectedProduct.capacity}</div>
                </div>
                <div className="bg-gray-800/40 border border-cyan-500/20 rounded-xl p-4 text-center">
                  <i className="ri-price-tag-3-line text-2xl text-cyan-400 mb-2"></i>
                  <div className="text-xs text-gray-400 mb-1">Starting Price</div>
                  <div className="text-sm font-semibold text-white break-words">{selectedProduct.price}</div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to={`/contact?product=${encodeURIComponent(selectedProduct.name)}`}
                  className="flex-1 px-8 py-4 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_30px_rgba(0,217,255,0.6)] whitespace-nowrap cursor-pointer text-center"
                >
                  Request Quote
                </Link>
                <button
                  onClick={closeModal}
                  className="flex-1 px-8 py-4 bg-gray-800/60 border border-cyan-500/30 text-white text-base font-medium rounded-full hover:bg-gray-700/60 hover:border-cyan-400/50 transition-all whitespace-nowrap cursor-pointer"
                >
                  Continue Browsing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
